import React, { useEffect, useState, useMemo } from "react";
import { Portal } from "@/lib/api";
import { FileText, Download, Search } from "lucide-react";
import { toast } from "sonner";

const PortalManuals = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    (async () => {
      try { setDocs(await Portal.docs.list()); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed to load documents"); }
      finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(docs.map((d) => d.category || "Manuals"));
    return ["All", ...Array.from(set).sort()];
  }, [docs]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return docs.filter((d) => {
      if (category !== "All" && (d.category || "Manuals") !== category) return false;
      if (!needle) return true;
      return (d.title + " " + d.description).toLowerCase().includes(needle);
    });
  }, [docs, q, category]);

  return (
    <section className="p-10" data-testid="portal-manuals-tab">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Portal</div>
        <h1 className="font-display font-black text-4xl text-white">Manuals &amp; PDFs</h1>
        <p className="text-slate-400 text-sm mt-2">Download product manuals, spec sheets, and reference documents.</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search documents…"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
            data-testid="portal-manuals-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                category === c
                  ? "bg-atc-blue text-white"
                  : "bg-slate-900/60 text-slate-300 border border-white/10 hover:border-white/25"
              }`}
              data-testid={`portal-manuals-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="portal-manuals-empty">
          No documents uploaded yet. Check back soon.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {filtered.map((d) => (
            <a
              key={d.id}
              href={d.file_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-4 p-5 glass rounded-xl border border-white/5 hover:border-atc-blue/40 transition group"
              data-testid={`portal-doc-card-${d.id}`}
            >
              <div className="h-12 w-12 rounded-lg bg-atc-blue/15 border border-atc-blue/30 flex items-center justify-center shrink-0">
                <FileText className="h-6 w-6 text-atc-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{d.category || "Manuals"} · {(d.file_type || "PDF").toUpperCase()}</div>
                <div className="text-white text-sm font-semibold mt-0.5">{d.title}</div>
                {d.description && <div className="text-xs text-slate-400 mt-1 line-clamp-2">{d.description}</div>}
              </div>
              <Download className="h-4 w-4 text-slate-500 group-hover:text-white transition mt-1 shrink-0" />
            </a>
          ))}
        </div>
      )}
    </section>
  );
};

export default PortalManuals;
